import React, { FunctionComponent, useEffect, useState } from 'react';
import CancelSubscriptionDialog from '../../../components/cancel-subscription-dialog/CancelSubscriptionDialog';
import PricingModal from '../../../components/pricing-modal/pricingModal';
import { usePaywallContext } from '../../../contexts/paywall';
import { useAccount } from '../../../hooks/useAccount';
import { useCancelSubscription, useCheckoutFlow } from '../../../hooks/useAccountMutations';
import SettingsPageLayout from '../../../layouts/settings/settingsPageLayout';
import { formatDate, parseZonedDate } from '../../../services/date';
import {
  Subscription,
  SubscriptionInterval,
  SubscriptionStatus,
} from '../../../services/models/subscription';
import { classNames } from '../../../utils/class-names';

const PlanPage: FunctionComponent = () => {
  const { paywallModalShown, setPaywallModalShown } = usePaywallContext();

  const { account: currentAccount } = useAccount();
  const account = currentAccount!;
  const { cancelSubscription, isCancelingSubscription } = useCancelSubscription();
  const { initUpdatePaymentSettings } = useCheckoutFlow();
  const product = account.product;
  const { trial, trialDaysLeft } = account.subscription;
  const isCurrentPlanMonthly = account.subscription.interval !== SubscriptionInterval.Yearly;

  const [showCancelSubsDialog, setShowCancelSubsDialog] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);

  useEffect(() => {
    if (paywallModalShown) {
      setPaywallModalShown(false);
      setShowPricingModal(true);
    }
  }, [paywallModalShown, setPaywallModalShown]);

  const getPlanActionButton = (subscription: Subscription) => {
    const { trial, canceled, active } = subscription;

    let text = '';

    if (trial) {
      text = 'Upgrade';
    } else if (active && !canceled) {
      text = 'Change';
    } else if (canceled && !active) {
      text = 'Buy it again';
    } else {
      return null;
    }

    return (
      <button
        type="button"
        className="bg-white rounded-md font-medium text-secondary-dark hover:text-secondary focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
        onClick={() => {
          setShowPricingModal(true);
        }}
      >
        {text}
      </button>
    );
  };

  const isActiveSubscription = () => {
    return (
      account && account.subscription && account.subscription.active && !account.subscription.trial
    );
  };

  const shouldShowCancelationSection = () => {
    return (
      isActiveSubscription() &&
      !account.subscription.canceled &&
      account.subscription?.planId !== 'TIER_API_CUSTOM'
    );
  };

  const getSubscriptionNextInvoiceDate = () => {
    const date = parseZonedDate(account.subscription.currentPeriodEnd);

    const dateText = !!date
      ? formatDate(date, {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        })
      : '';

    return dateText;
  };

  const getSubscriptionStatus = (subscription: Subscription) => {
    let statusLabel;
    let statusCss;

    switch (subscription?.status) {
      case SubscriptionStatus.Active: {
        statusLabel = 'Active';
        statusCss = 'text-green-600';
        break;
      }
      case SubscriptionStatus.PastDue: {
        statusLabel = 'Past Due';
        statusCss = 'text-yellow-700';
        break;
      }
      case SubscriptionStatus.Canceled: {
        statusLabel = 'Canceled';
        statusCss = 'text-red-700';
        break;
      }
      default: {
        statusLabel = subscription?.status;
        statusCss = 'text-gray-900';
        break;
      }
    }

    return <span className={classNames(statusCss)}>{statusLabel}</span>;
  };

  return (
    <SettingsPageLayout title="Plan & Billing | Newslit">
      {/* Past due alert */}
      {!!account?.subscription?.pastDue && (
        <div className="rounded-md bg-red-50 p-4 mt-5">
          <div className="flex">
            <div className="shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-bold text-red-800">
                Oh no! Your payment method recently failed to process.
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>Update your payment information below</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plan Info */}
      <div className="mt-10 divide-y divide-gray-200">
        <div className="space-y-1">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Plan</h3>
          {trial && trialDaysLeft > 0 && (
            <p className="max-w-2xl text-sm text-gray-500 m-0">
              You have <span className="font-bold">{trialDaysLeft}</span> day
              {trialDaysLeft > 1 ? 's' : ''} left in your trial
            </p>
          )}
          {trial && trialDaysLeft === 0 && (
            <p className="max-w-2xl text-sm text-gray-500 m-0">
              Your trial expires <span className="font-bold">Today</span>
            </p>
          )}
          {trial && trialDaysLeft < 0 && (
            <p className="max-w-2xl text-sm text-gray-500 m-0">Your trial has expired</p>
          )}
        </div>

        <div className="mt-6">
          <dl className="divide-y divide-gray-200">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-gray-500">Name</dt>
              <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className="grow">
                  <span className="font-bold">{product?.name}</span>
                  {!trial && (
                    <>
                      {' '}
                      via{' '}
                      <span className="capitalize">
                        {!!account?.subscription?.cardOnFile && (
                          <>
                            {isCurrentPlanMonthly
                              ? SubscriptionInterval.Monthly
                              : SubscriptionInterval.Yearly}
                            {'ly '}
                            Subscription
                          </>
                        )}
                        {!account?.subscription?.cardOnFile && <>Custom Susbscription</>}
                      </span>
                    </>
                  )}
                </span>
                <span className="ml-4 shrink-0">{getPlanActionButton(account.subscription)}</span>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {isActiveSubscription() && (
        // Billing Info
        <div className="mt-10 divide-y divide-gray-200">
          <div className="space-y-1">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Billing</h3>
          </div>

          <div className="mt-6">
            <dl className="divide-y divide-gray-200">
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-medium text-gray-500">
                  {account?.subscription?.canceled ? 'Expires at' : 'Next invoice'}
                </dt>
                <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <span className="grow">{getSubscriptionNextInvoiceDate()}</span>
                  {shouldShowCancelationSection() && (
                    <span className="ml-4 shrink-0">
                      <button
                        type="button"
                        className="bg-white rounded-md font-medium text-secondary-dark hover:text-secondary focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                        onClick={() => setShowCancelSubsDialog(true)}
                      >
                        Cancel subscription
                      </button>
                    </span>
                  )}
                </dd>
              </div>

              {!!account?.subscription?.cardOnFile && (
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5">
                  <dt className="text-sm font-medium text-gray-500">Card information</dt>
                  <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <span className="grow">
                      <span className="font-bold capitalize">
                        {account?.subscription?.cardOnFile.brand}
                      </span>{' '}
                      ending in{' '}
                      <span className="font-['Ubuntu'] text-[15px] font-bold tracking-[0.5px] text-black">
                        {account?.subscription?.cardOnFile?.lastFourDigits}
                      </span>
                    </span>
                    <span className="ml-4 shrink-0">
                      <button
                        type="button"
                        className="bg-white rounded-md font-medium text-secondary-dark hover:text-secondary focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                        onClick={() => initUpdatePaymentSettings(window.location.href)}
                      >
                        Edit payment method
                      </button>
                    </span>
                  </dd>
                </div>
              )}

              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5">
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {getSubscriptionStatus(account.subscription)}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}

      {showCancelSubsDialog && (
        <CancelSubscriptionDialog
          account={account}
          open={showCancelSubsDialog}
          isCanceling={isCancelingSubscription}
          onAccept={async () => {
            // Keep the dialog open on failure so the user can retry; the
            // error itself surfaces via the global ErrorReporter.
            const canceled = await cancelSubscription();
            if (canceled) {
              setShowCancelSubsDialog(false);
            }
          }}
          onCancel={() => setShowCancelSubsDialog(false)}
        />
      )}

      <PricingModal isOpen={showPricingModal} onClose={() => setShowPricingModal(false)} />
    </SettingsPageLayout>
  );
};

export default PlanPage;
